import { DeleteOutlined, DownloadOutlined, FileOutlined, FilePdfOutlined } from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { Button, Image, Modal } from 'antd'
import { icons } from 'antd/es/image/PreviewGroup'
import { context } from 'rc-image/es/PreviewGroup'
import React from 'react'

import s from './file-display.module.less'
import { fileDownload } from './file-download'
import { ImagePreview } from './image-preview.component'

interface FileDisplayCommonProps {
  file: any
  images?: any[]
  autoplay?: boolean
}

interface FileDisplayProps extends FileDisplayCommonProps {
  width: number | string
  height: number | string
  progress?: number | null
  showClose?: boolean
  showDownload?: boolean
  onClose?: () => void
}

interface PreviewUrl {
  url: string
  canPreview: boolean
}

const PostPopup: React.FC<{ element: React.ReactElement; width?: string }> = ({ children, element, width }) => {
  const [visible, { toggle }] = useToggle()
  return (
    <>
      {React.cloneElement(children as any, {
        onClick: toggle,
      })}
      <Modal
        visible={visible}
        centered
        width={width}
        footer={null}
        bodyStyle={{ padding: 0 }}
        onClose={() => toggle()}
        onCancel={() => toggle()}
        destroyOnClose
      >
        {element}
      </Modal>
    </>
  )
}

const { Provider } = context

const FileDisplayImage: React.FC<FileDisplayCommonProps> = ({ file, images }) => {
  const [previewUrls, setPreviewUrls] = React.useState<Map<number, PreviewUrl>>(
    new Map(images ? images.map((img) => [img.fileId, { url: img.url, canPreview: true }]) : [])
  )
  const [current, setCurrent] = React.useState<number>(file.fileId)
  const [isShowPreview, setShowPreview] = React.useState(false)
  const [mousePosition, setMousePosition] = React.useState<null | { x: number; y: number }>(null)
  const canPreviewUrls = new Map<number, string>(
    Array.from(previewUrls)
      .filter(([, { canPreview }]) => !!canPreview)
      .map(([id, { url }]) => [id, url])
  )
  const registerImage = (id: number, url: string, canPreview = true) => {
    const unRegister = () => {
      setPreviewUrls((oldPreviewUrls) => {
        const clonePreviewUrls = new Map(oldPreviewUrls)
        const deleteResult = clonePreviewUrls.delete(id)
        return deleteResult ? clonePreviewUrls : oldPreviewUrls
      })
    }

    setPreviewUrls((oldPreviewUrls) => {
      return new Map(oldPreviewUrls).set(id, {
        url,
        canPreview,
      })
    })

    return unRegister
  }

  React.useEffect(() => {
    setPreviewUrls(new Map(images ? images.map((img) => [img.fileId, { url: img.url, canPreview: true }]) : []))
  }, [images])

  return (
    <>
      {images && images.length > 0 ? (
        <Provider
          value={{
            isPreviewGroup: true,
            previewUrls: canPreviewUrls,
            setPreviewUrls: () => {},
            current,
            setCurrent,
            setShowPreview,
            setMousePosition,
            registerImage,
          }}
        >
          <Image className={s.imageContainer} preview={false} src={file.url} onClick={() => setShowPreview(true)} />
          <ImagePreview
            aria-hidden={!isShowPreview}
            visible={isShowPreview}
            prefixCls='ant-image-preview'
            onClose={(e) => {
              e.stopPropagation()
              setShowPreview(false)
              setCurrent(file.fileId)
              setMousePosition(null)
            }}
            mousePosition={mousePosition || undefined}
            src={canPreviewUrls.get(current)}
            icons={icons}
            transitionName=''
            maskTransitionName=''
          />
        </Provider>
      ) : (
        <Image className={s.imageContainer} preview={{ src: file.url }} src={file.url} />
      )}
    </>
  )
}

const FileDisplayPdf: React.FC<FileDisplayCommonProps> = ({ file }) => {
  const element = <embed src={file.url} width='100%' height='100%' style={{ height: '100vh' }} />
  return (
    <PostPopup element={element} width='80vw'>
      <div className={s.defaultElement}>
        <FilePdfOutlined className={s.icon} />
        {file.name}
      </div>
    </PostPopup>
  )
}

const FileDisplayVideo: React.FC<FileDisplayCommonProps> = ({ file, autoplay }) => {
  return (
    <div className={s.videoContainer}>
      <video autoPlay={autoplay} controls>
        <source src={file.url} type={file.type} />
      </video>
    </div>
  )
}

const FileDisplayDefault: React.FC<FileDisplayCommonProps> = ({ file }) => {
  return (
    <div className={s.defaultElement}>
      <FileOutlined className={s.icon} />
      {file.name}
    </div>
  )
}

export const FileDisplay: React.FC<FileDisplayProps> = ({
  file,
  images,
  width,
  height,
  showDownload,
  showClose,
  autoplay = false,
  onClose,
  progress,
}) => {
  const hasProgress = !!progress && progress > 0 && progress < 1

  const progressElement = hasProgress && <div className={s.progress}>{Math.floor((progress || 0) * 100)}%</div>

  const closeButton = showClose && (
    <Button shape='circle' size='small' danger icon={<DeleteOutlined />} onClick={onClose} />
  )

  const [type, subtype] = file.type.split('/')
  let Element = FileDisplayDefault
  switch (type) {
    case 'image':
      showDownload = false
      Element = FileDisplayImage
      break
    case 'video':
      Element = FileDisplayVideo
      break
    case 'application':
      switch (subtype) {
        case 'pdf':
          Element = FileDisplayPdf
      }
  }

  const downloadButton = showDownload && (
    <Button
      shape='circle'
      size='small'
      icon={<DownloadOutlined />}
      onClick={(e) => {
        e.preventDefault()
        fileDownload({ url: file.url, name: file.name, type: file.type })
      }}
    />
  )

  return (
    <div className={s.container} style={{ width, height }}>
      {progressElement}
      <div className={s.buttons}>
        {downloadButton}
        {closeButton}
      </div>
      <Element file={file} images={images} autoplay={autoplay} />
    </div>
  )
}
